import React from "react";

import { Button } from "~/components/button";
import { SystemScreen } from "~/components/system-screen";
import { secondsToMilliseconds } from "~/tools/date-time";
import { useScoped } from "~/tools/impulse";

import { queryLiveness } from "../../queries";
import { useQuery } from "../../queries/react-query";
import { Link } from "../react/link";

import { isMaintenanceBoundaryActive } from "./state";

export interface MaintenanceBoundaryProps {
	/**
	 * The interval in ms at which the liveness check is performed.
	 *
	 * @default 5s
	 */
	livenessCheckInterval?: number;
	children: React.ReactNode;
}

export const MaintenanceBoundary: React.FC<MaintenanceBoundaryProps> = ({
	livenessCheckInterval = secondsToMilliseconds(5),
	children,
}) => {
	const isActive = useScoped(isMaintenanceBoundaryActive);

	const {
		/**
		 * It cannot use isError because retry=true, so the error is never set.
		 *
		 * @link https://tanstack.com/query/v3/docs/framework/react/reference/useQuery
		 * - Incremented every time the query fails.
		 * - Reset to 0 when the query succeeds.
		 */
		failureCount,
	} = useQuery({
		...queryLiveness,
		// query liveness only when the flag is on
		enabled: isActive,
		retry: true,
		retryDelay: livenessCheckInterval,
		onSuccess: () => {
			isMaintenanceBoundaryActive.setValue(false);
		},
	});

	/**
	 * Show the maintenance screen only when the liveness query fails,
	 * not when isMaintenanceBoundaryActive is true.
	 */
	if (failureCount === 0) {
		return <>{children}</>;
	}

	return (
		<SystemScreen
			alertContent={
				<div className="leading-[21px]">
					<strong className="font-semibold">Our service is currently undergoing maintenance.</strong> This usually takes
					just a few minutes. You will be redirected to{" "}
					<Link el="button" onClick={() => isMaintenanceBoundaryActive.setValue(false)} theme="secondary" type="button">
						your instance
					</Link>{" "}
					shortly.
				</div>
			}
			alertVariant="warning"
			className="min-h-full"
		>
			<p className="mb-0">
				We recommend you to join the <strong className="font-semibold">#observability</strong> channel of our{" "}
				<strong className="font-semibold">Slack&nbsp;community</strong> to learn more about SUSE Observability, ask
				questions and troubleshoot problems with your peers.
			</p>

			<Button asChild className="mt-10 min-w-36 uppercase" variant="primary">
				<a href="https://slack.rancher.io" rel="noopener noreferrer" target="_blank">
					Join us
				</a>
			</Button>
		</SystemScreen>
	);
};
